import React from "react";
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Project } from "../../types/project.types";

interface ProjectCardProps {
  project: Project;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onView,
  onEdit,
  onDelete,
}) => {
  const budgetAmount = project.budget?.amount || 0;
  const budgetSpent = project.spending?.current || 0;
  const budgetUsagePercentage =
    budgetAmount > 0 ? (budgetSpent / budgetAmount) * 100 : 0;
  const isOverBudget = budgetUsagePercentage > 100;
  const isNearLimit = budgetUsagePercentage > 80 && !isOverBudget;

  const getBudgetColor = () => {
    if (isOverBudget)
      return "text-danger-700 dark:text-danger-300 bg-gradient-danger/20 border-danger-200/30";
    if (isNearLimit)
      return "text-warning-700 dark:text-warning-300 bg-gradient-warning/20 border-warning-200/30";
    return "text-success-700 dark:text-success-300 bg-gradient-success/20 border-success-200/30";
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <div className="glass rounded-xl shadow-xl border border-primary-200/30 backdrop-blur-xl bg-gradient-light-panel dark:bg-gradient-dark-panel hover:scale-105 transition-all duration-300 hover:border-primary-300/50 overflow-hidden">
      <div className="p-4 sm:p-5 md:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start mb-4 sm:mb-6 gap-3 sm:gap-0">
          <div className="flex-1 min-w-0 w-full sm:w-auto">
            <div className="flex gap-2 sm:gap-3 items-center mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-primary flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-white font-display font-bold text-base sm:text-lg">
                  {project.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl font-display font-bold gradient-text-primary truncate mb-1">
                  {project.name}
                </h3>
                <span
                  className={`px-2 sm:px-3 py-0.5 sm:py-1 text-xs font-display font-bold rounded-full border ${project.isActive
                    ? "bg-gradient-success/20 text-success-700 dark:text-success-300 border-success-200/30"
                    : "bg-gradient-secondary/20 text-secondary-700 dark:text-secondary-300 border-secondary-200/30"
                    }`}
                >
                  {project.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
            {project.description && (
              <p className="text-sm sm:text-base font-body text-light-text-secondary dark:text-dark-text-secondary line-clamp-2">
                {project.description}
              </p>
            )}
          </div>
          <div className="flex gap-2 sm:ml-4 w-full sm:w-auto justify-end sm:justify-start">
            <button
              onClick={onView}
              className="p-2.5 glass border border-primary-200/30 dark:border-primary-500/20 backdrop-blur-xl bg-gradient-light-panel dark:bg-gradient-dark-panel text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-500/10 dark:hover:bg-primary-500/20 transition-all duration-300 transform hover:scale-110 active:scale-95 shadow-sm hover:shadow-md"
              title="View project"
            >
              <EyeIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onEdit}
              className="p-2.5 glass border border-success-200/30 dark:border-success-500/20 backdrop-blur-xl bg-gradient-light-panel dark:bg-gradient-dark-panel text-success-600 dark:text-success-400 rounded-lg hover:bg-success-500/10 dark:hover:bg-success-500/20 transition-all duration-300 transform hover:scale-110 active:scale-95 shadow-sm hover:shadow-md"
              title="Edit project"
            >
              <PencilIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onDelete}
              className="p-2.5 glass border border-danger-200/30 dark:border-danger-500/20 backdrop-blur-xl bg-gradient-light-panel dark:bg-gradient-dark-panel text-danger-600 dark:text-danger-400 rounded-lg hover:bg-danger-500/10 dark:hover:bg-danger-500/20 transition-all duration-300 transform hover:scale-110 active:scale-95 shadow-sm hover:shadow-md"
              title="Delete project"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Budget Progress */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <span className="font-display font-medium text-light-text-primary dark:text-dark-text-primary">
              Budget Usage
            </span>
            <span
              className={`px-3 py-1 text-xs font-display font-bold rounded-full border ${getBudgetColor()}`}
            >
              {budgetUsagePercentage.toFixed(0)}%
              {isOverBudget && (
                <ExclamationTriangleIcon className="inline ml-1 w-3 h-3" />
              )}
            </span>
          </div>
          <div className="w-full h-3 bg-light-background-secondary dark:bg-dark-background-secondary rounded-full overflow-hidden">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${isOverBudget
                ? "bg-gradient-danger"
                : isNearLimit
                  ? "bg-gradient-warning"
                  : "bg-gradient-success"
                }`}
              style={{ width: `${Math.min(budgetUsagePercentage, 100)}%` }}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
          <div className="glass rounded-lg p-3 sm:p-4 border border-secondary-200/30 dark:border-secondary-500/20 shadow-lg backdrop-blur-xl bg-gradient-light-panel dark:bg-gradient-dark-panel text-center">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-gradient-secondary flex items-center justify-center shadow-lg mx-auto mb-1 sm:mb-2">
              <UserGroupIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
            </div>
            <p className="text-base sm:text-lg font-display font-bold text-secondary-900 dark:text-white">
              {project.tags?.length || 0}
            </p>
            <p className="text-xs font-body text-secondary-600 dark:text-secondary-300">Tags</p>
          </div>
          <div className="glass rounded-lg p-3 sm:p-4 border border-success-200/30 dark:border-success-500/20 shadow-lg backdrop-blur-xl bg-gradient-light-panel dark:bg-gradient-dark-panel text-center">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-gradient-success flex items-center justify-center shadow-lg mx-auto mb-1 sm:mb-2">
              <CurrencyDollarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
            </div>
            <p className="text-sm sm:text-lg font-display font-bold text-success-600 dark:text-success-400 truncate">
              {formatCurrency(budgetAmount)}
            </p>
            <p className="text-xs font-body text-secondary-600 dark:text-secondary-300">Budget</p>
          </div>
          <div className="glass rounded-lg p-3 sm:p-4 border border-accent-200/30 dark:border-accent-500/20 shadow-lg backdrop-blur-xl bg-gradient-light-panel dark:bg-gradient-dark-panel text-center">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-gradient-accent flex items-center justify-center shadow-lg mx-auto mb-1 sm:mb-2">
              <ArrowTrendingUpIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
            </div>
            <p className="text-sm sm:text-lg font-display font-bold text-accent-600 dark:text-accent-400 truncate">
              {formatCurrency(budgetSpent)}
            </p>
            <p className="text-xs font-body text-secondary-600 dark:text-secondary-300">Spent</p>
          </div>
        </div>

        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            {project.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 text-xs font-display font-medium bg-gradient-primary/20 text-primary-700 dark:text-primary-300 border border-primary-200/30 rounded-full"
              >
                {tag}
              </span>
            ))}
            {project.tags.length > 3 && (
              <span className="px-3 py-1 text-xs font-display font-medium bg-gradient-secondary/20 text-secondary-700 dark:text-secondary-300 border border-secondary-200/30 rounded-full">
                +{project.tags.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Period indicator */}
        {project.budget?.period && (
          <div className="pt-4 mt-6 border-t border-primary-200/30">
            <div className="glass rounded-lg p-3 border border-primary-200/30 shadow-lg backdrop-blur-xl text-center">
              <p className="font-body text-light-text-secondary dark:text-dark-text-secondary text-sm">
                Budget period: <span className="font-display font-semibold gradient-text-primary">{project.budget.period}</span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
